import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StandardTrainingCategoryTypeModel } from 'src/app/models/standard-training-category/standard-training-category-type.model';
import { StandardTrainingCategoryViewModel } from 'src/app/models/standard-training-category/standard-training-category-view.model';

@Component({
  selector: 'app-standard-category-selector-modal',
  standalone: false,
  templateUrl: './standard-category-selector-modal.component.html',
  styleUrl: './standard-category-selector-modal.component.scss',
})
export class StandardCategorySelectorModalComponent implements OnChanges {
  @Input() showModal = false;

  @Input() selectedCategoryId: string | null = null;
  @Input() selectedCategoryIds: string[] = [];
  @Input() selectionMode: 'single' | 'multiple' = 'single';

  @Input() usedStandardCategories: StandardTrainingCategoryViewModel[] = [];
  @Input() showUsedStandardCategories: boolean = false;

  @Output() 
  categorySelected = new EventEmitter<StandardTrainingCategoryViewModel>();

  @Output() 
  categoriesSelected = new EventEmitter<StandardTrainingCategoryViewModel[]>();

  @Input()
  isAssociatedCategory: boolean = false;

  @Input()
  standardCategories: StandardTrainingCategoryViewModel[] = [];

  @Output() 
  showModalChange = new EventEmitter<boolean>();

  selectedCategories: StandardTrainingCategoryViewModel[] = [];

  readonly maxVisibleStandardCategories = 7;
  showMoreUsedStandardCategories: boolean = false;

  get filteredUsedStandardCategories(): StandardTrainingCategoryViewModel[] {
    const search = this.search.toLowerCase();

    if (!search) {
      return this.usedStandardCategories;
    }

    return this.usedStandardCategories.filter(x =>
      x.name.toLowerCase().includes(search) ||
      x.description?.toLowerCase().includes(search) ||
      x.descriptionExtended?.toLowerCase().includes(search)
    );
  }

  get visibleStandardCategories(): StandardTrainingCategoryViewModel[] {
    return this.filteredUsedStandardCategories.slice(
      0,
      this.maxVisibleStandardCategories
    );
  }

  search = '';
  selectedTab = 0;
  tabs: any[] = [
    {
      id: 0,
      text: this.translateService.instant(
        'STANDARD-CATEGORIES.STANDARD-CATEGORY-TYPES.PRIVATE-INSURANCES'
      ),
      count: 0
    },
    {
      id: 1,
      text: this.translateService.instant(
        'STANDARD-CATEGORIES.STANDARD-CATEGORY-TYPES.CORPORATE-INSURANCES'
      ),
      count: 0
    }
  ];

  privateTree: any[] = [];
  corporateTree: any[] = [];
  displayedTree: any[] = [];

  constructor(
    private translateService: TranslateService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['standardCategories']) {
      this.buildTrees();
    }
  }

  toggleShowMoreUsedStandardCategories() {
    this.showMoreUsedStandardCategories = 
      !this.showMoreUsedStandardCategories;
  }

  selectUsedStandardCategory(
    category: StandardTrainingCategoryViewModel
  ) {
    category.selected = !category.selected;

    if (this.selectionMode === 'multiple') {
      this.onStandardCategorySelectionChanged(category);
    } else {
      this.onStandardCategoryClick(category);
    }

    this.buildTrees();
  }

  close(): void {
    this.showModal = false;
    this.showModalChange.emit(false);
  }

  onSearchChanged(e: any): void {
    this.search = e.value ?? '';
    this.buildTrees();
  }

  onTabChanged(e: any): void {
    this.updateDisplayedTree();
  }

  onStandardCategoryClick(category: any): void {
    if (this.selectionMode !== 'single' || category.isGroup) {
      return;
    }

    this.categorySelected.emit(category.category);
    this.close();
  }

  onStandardCategorySelectionChanged(category: any): void {
    if (category.isGroup) {
      return;
    }

    if (category.selected) {
      if (!this.selectedCategoryIds.includes(category.id)) {
        this.selectedCategoryIds = [
          ...this.selectedCategoryIds,
          category.id
        ];
      }
    } else {
      this.selectedCategoryIds = this.selectedCategoryIds.filter(
        id => id !== category.id
      );
    }

    const usedCategory = this.usedStandardCategories.find(
      x => x.id === category.id
    );

    if (usedCategory) {
      usedCategory.selected = category.selected;
    }

    this.selectedCategories = this.standardCategories.filter(x =>
      this.selectedCategoryIds.includes(x.id)
    );
  }
  
  confirmSelection() {
    this.categoriesSelected.emit(this.selectedCategories);
    this.close();
  }

  private buildTrees(): void {
    this.setTreeViewValues();

    const privateCategories = this.standardCategories.filter(
      x => x.type === StandardTrainingCategoryTypeModel.PrivateInsurance
    );

    const corporateCategories = this.standardCategories.filter(
      x => x.type === StandardTrainingCategoryTypeModel.CorporateInsurance
    );

    this.tabs[0].count = privateCategories.filter(x => x.parentId).length;
    this.tabs[1].count = corporateCategories.filter(x => x.parentId).length;

    this.privateTree = this.createTree(privateCategories);
    this.corporateTree = this.createTree(corporateCategories);

    this.updateDisplayedTree();
  }

  private updateDisplayedTree(): void {
    this.displayedTree = this.selectedTab === 0
      ? this.privateTree
      : this.corporateTree;
  }

  private setTreeViewValues(): void {
    this.standardCategories.forEach(category => {
      category.selected =
        this.selectedCategoryIds.includes(category.id) ||
        this.selectedCategoryId === category.id;
    });
  }

  private createTree(
    categories: StandardTrainingCategoryViewModel[]
  ): any[] {
    const search = this.search.toLowerCase();

    const parents = categories
      .filter(x => !x.parentId)
      .sort((a, b) => a.name.localeCompare(b.name));

    return parents
      .map(parent => {
        const children = categories
          .filter(x => x.parentId === parent.id)
          .filter(x =>
            !search ||
            x.name.toLowerCase().includes(search) ||
            x.description.toLowerCase().includes(search) ||
            x.descriptionExtended.toLowerCase().includes(search)
          )
          .sort((a, b) => a.name.localeCompare(b.name));

        if (children.length === 0) {
          return null;
        }

        return {
          id: parent.id,
          text: parent.name,
          expanded: search && children.length > 0,
          isGroup: true,
          count: children.length,
          items: children.map(child => ({
            id: child.id,
            text: child.name,
            category: child,
            selected: child.selected
          }))
        };
      })
      .filter(x => x != null);
  }

  onItemRendered(e: any): void {
    if (e.itemData.isGroup) {
      e.itemElement.classList.add('tree-item-group-node');

      const checkbox = e.itemElement.querySelector('.dx-checkbox');
      if (checkbox) {
        checkbox.style.display = 'none';
      }
    }
  }
}